"use client";

import "@/components/ui/molecules/categories/data-display.css";
import { resolveNextImage } from "@/lib/next/resolve-next-image";

import { useBrandImages } from "@/components/ui/providers/BrandImagesProvider";
import { useTenantBrandingOptional } from "@/lib/tenant/branding";
import { DISPUTEWELL_PLATFORM_BRAND, PLATFORM_NAME } from "@/lib/theme/branding";
import { cn } from "@/utils/cn";
import Image from "next/image";

import * as React from "react";

import { memo } from "react";

const NextImage = resolveNextImage(Image);

/**
 * OrgLogo — logo primitive for tenant-aware UI surfaces.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see
 * .claude/agent-prompts/molecule-authoring.md). Owns the initials-circle
 * fallback's structural CSS via the `.dw-org-logo__initials` BEM sub-element.
 * Image render paths delegate to next/image + Tailwind dark-mode utilities
 * (see "decision 4" below) — the third-party `<Image>` component shape
 * isn't aggressively wrapped.
 *
 * Resolution order (first non-null wins):
 *   1. `override` prop (for pre-auth contexts where TenantBrandingProvider is not in scope —
 *      accept-invite, reset-password, AuthPage CRM variant before login)
 *   2. `useTenantBrandingOptional()` context (CRM, portal, onboarding)
 *   3. Fallback per `fallback` prop:
 *      - 'default-brand': render DisputeWellLogo (use for chrome that should still show DW
 *        when no tenant brand is set, e.g., the platform's own org)
 *      - 'initials': render org initials in a circle filled with the org's primary color
 *        (use for client-facing surfaces where the DW logo would leak the underlying platform)
 *
 * A surface that shows the platform's logo whatever the organization renders DisputeWellLogo,
 * below, directly.
 *
 * Design decisions for the class-selector migration:
 *   1. Default export DROPPED — molecule-authoring.md forbids default exports;
 *      all consumers verified to use named imports prior to migration.
 *   2. DW_PRIMARY (#f97316) KEPT as TSX literal — brand-orange is not a
 *      design-system color token candidate (the token registry doesn't model
 *      "platform's own brand orange" as a separate vocabulary). Passed through
 *      to CSS via the --dw-org-logo-bg custom property; CSS uses the same hex
 *      as the var() fallback so the literal is the single source.
 *   3. Inline style block (width / height / backgroundColor / fontSize / lineHeight)
 *      TRANSLATED to CSS-var injection. Runtime-derived prop values
 *      (height-based size, computed font-size, primaryColor) flow into CSS via
 *      `--dw-org-logo-*` custom properties; CSS reads them with safe fallbacks.
 *   4. Tailwind dark-mode utilities (`dark:hidden`, `hidden dark:block`) REPLACED
 *      with `.dw-org-logo__light-img` / `.dw-org-logo__dark-img` BEM classes
 *      per pure-BEM directive. CSS handles dark swap via `.dark` class selector.
 *      The original carve-out ("third-party next/image shape") is superseded.
 *
 * forwardRef target: `HTMLDivElement` — the ref applies only when the
 * initials-circle path renders (`<div className="dw-org-logo__initials" />`).
 * Image paths render `<Image>` (Next.js, forwards its own ref to `<img>`); the
 * forwarded ref does not apply uniformly across all 3 paths. Document and
 * accept the limitation rather than complicate the API.
 *
 * Source: docs/implementation-blueprint.md §8 (molecule contract);
 *         .claude/agent-prompts/molecule-authoring.md (binding template);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 * src/components/ui/molecules/PageHeader.tsx (molecule precedent);
 *         src/components/ui/atoms/UserAvatar.tsx (atom precedent for runtime-color circular avatar).
 */

const DW_PRIMARY = "#f97316";

/**
 * The platform wordmark's two images as they stand now: the brand kit this browser wears (User
 * settings → Appearance → Brand images), else the platform's own. A kit's light logo with no dark one
 * serves both surfaces, as one organization logo does.
 */
export function useDisputeWellLogoUrls(): { light: string; dark: string } {
    const images = useBrandImages();
    const light = images.disputewellLogo?.url ?? DISPUTEWELL_PLATFORM_BRAND.logoUrl;
    const dark = images.disputewellLogoDark?.url ?? images.disputewellLogo?.url ?? DISPUTEWELL_PLATFORM_BRAND.logoDarkUrl;
    return { light, dark };
}

/**
 * DisputeWellLogo — the platform's own wordmark, wherever DisputeWell's logo shows rather than an
 * organization's: the CRM and admin sidebars, help, the sign-in pages, the "Powered by" line and the
 * marketing site. Each mode shows the image made for its surface. `surface="dark"` is for artwork
 * that is dark in both modes: it shows the dark-surface image alone.
 *
 * Both images skip the optimizer: the platform's are SVGs, and a brand kit's are served from the
 * deployment's file storage.
 */
export const DisputeWellLogo = memo(function DisputeWellLogo(
    { width = 140, height = 32, className, priority, surface }: {
        /** Intrinsic width in px (forwarded to next/image). Default: 140. */
        width?: number;
        /** Intrinsic height in px (forwarded to next/image). Default: 32. */
        height?: number;
        className?: string;
        priority?: boolean;
        surface?: "dark";
    }
) {
    const { light, dark } = useDisputeWellLogoUrls();
    if (surface === "dark") {
        return <NextImage src={dark} alt={PLATFORM_NAME} width={width} height={height} className={className} priority={priority} unoptimized data-component="DisputeWellLogo" data-location="src/components/ui/molecules/OrgLogo.tsx"/>;
    }
    return (
        <>
            <NextImage src={light} alt={PLATFORM_NAME} width={width} height={height} className={cn("dw-org-logo__light-img", className)} priority={priority} unoptimized data-component="DisputeWellLogo" data-location="src/components/ui/molecules/OrgLogo.tsx"/>
            <NextImage src={dark} alt={PLATFORM_NAME} width={width} height={height} className={cn("dw-org-logo__dark-img", className)} priority={priority} unoptimized/>
        </>
    );
});

type OrgLogoFallback = "default-brand" | "initials";

interface OrgLogoOverride {
    /** Light-mode logo URL. Pass null to skip image and use fallback. */
    logoUrl?: string | null;
    /** Optional dark-mode logo URL. Falls back to light logo if absent. */
    logoDarkUrl?: string | null;
    /** Display name. Used for alt text and initials fallback. Required. */
    name: string;
    /** Hex primary color (e.g., '#0066ff'). Used for the initials background. */
    primaryColor?: string | null;
}

interface OrgLogoProps {
    /**
     * What to render when no logo URL is available (no provider, no override, or
     * provider/override has null logoUrl). Default: 'default-brand'.
     */
    fallback?: OrgLogoFallback;
    /** Intrinsic width in px (forwarded to next/image). Default: 140. */
    width?: number;
    /** Intrinsic height in px (forwarded to next/image, also initials circle diameter). Default: 32. */
    height?: number;
    /** Tailwind classes applied to image / initials root. */
    className?: string;
    /** Image alt text override. Defaults to org name (or 'DisputeWell'). */
    alt?: string;
    /** Forward to next/image priority flag. */
    priority?: boolean;
    /**
     * Forward to next/image `unoptimized` flag. Set true for arbitrary remote URLs
     * (e.g., tenant-uploaded logos hosted outside the configured next.config
     * remotePatterns) to skip the Next image optimizer. Defaults to false; the DW
     * default-brand fallback is local and never needs this.
     */
    unoptimized?: boolean;
    /**
     * Inline brand override for pre-auth contexts where TenantBrandingProvider is not mounted
     * (accept-invite preview, reset-password landing, AuthPage CRM variant pre-login).
     * When provided, takes precedence over hook context.
     */
    override?: OrgLogoOverride;
}

/**
 * Extract up to 2 initials from a name. Exported for unit tests.
 *  - "Acme Credit Repair" → "AC"
 *  - "Solo" → "SO"
 *  - "" / whitespace → "?"
 */
function getInitials(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) {
        return "?";
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
        return parts[0]!.slice(0, 2).toUpperCase();
    }
    return parts.slice(0, 2).map((word) => word[0]?.toUpperCase() ?? "").join("");
}

export const OrgLogo = memo(
    React.forwardRef<HTMLDivElement, OrgLogoProps>(function OrgLogo({ fallback = "default-brand", width = 140, height = 32, className, alt, priority, unoptimized, override }, ref): React.JSX.Element | null {
        const ctx = useTenantBrandingOptional();

        // Resolve effective brand source — override beats provider beats nothing
        const logoUrl = override?.logoUrl ?? ctx?.brand.logoUrl ?? null;
        const logoDarkUrl = override?.logoDarkUrl ?? ctx?.brand.logoDarkUrl ?? null;
        const name = override?.name ?? ctx?.tenantName ?? PLATFORM_NAME;
        const primaryColor = override?.primaryColor ?? ctx?.brand.primaryColor ?? DW_PRIMARY;

        // Path 1: org logo present — render it (with optional dark variant).
        // With a distinct dark variant, each image is mode-gated; with a single
        // URL the one image must stay visible in BOTH modes (no mode class), or
        // dark mode would hide the only logo on the page.
        if (logoUrl) {
            const darkVariantUrl = logoDarkUrl ?? logoUrl;
            const altText = alt ?? name;
            if (darkVariantUrl === logoUrl) {
                return <NextImage src={logoUrl} alt={altText} width={width} height={height} className={className} priority={priority} unoptimized={unoptimized} data-component="OrgLogo" data-location="src/components/ui/molecules/OrgLogo.tsx"/>;
            }
            return (
                <>
                    <NextImage src={logoUrl} alt={altText} width={width} height={height} className={cn("dw-org-logo__light-img", className)} priority={priority} unoptimized={unoptimized} />
                    <NextImage src={darkVariantUrl} alt={altText} width={width} height={height} className={cn("dw-org-logo__dark-img", className)} priority={priority} unoptimized={unoptimized} />
                </>
            );
        }

        // Path 2: no logo — apply fallback
        if (fallback === "default-brand") {
            return <DisputeWellLogo width={width} height={height} className={className} priority={priority} />;
        }

        // fallback === 'initials'
        const initials = getInitials(name);
        return (
            <div
                ref={ref}
                className={cn("dw-org-logo__initials", className)}
                // eslint-disable-next-line no-restricted-syntax -- CSS-var injection for runtime-derived values (size, fontSize, primaryColor) per molecule-authoring.md class-selector pattern; the rule's "rules.md §13" citation references the SUPERSEDED legacy doc (AGENTS.md §1)
                style={{ "--dw-org-logo-size": `${height}px`, "--dw-org-logo-bg": primaryColor, "--dw-org-logo-font-size": `${Math.round(height * 0.4)}px` } as React.CSSProperties}
                aria-label={alt ?? name}
                role="img"
            >
                {initials}
            </div>
        );
    })
);
OrgLogo.displayName = "OrgLogo";
